import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import roleService from "../../services/api/roleService";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { QUERY_CONFIGS } from "../../lib/react-query/config";
import { useCallback } from "react";
import { profileQueryKeys } from "./useProfileQueries";
import { authQueryKeys } from "./useAuthQueries";

// Role Query Keys
export const roleQueryKeys = {
  all: ["roles"],
  list: () => [...roleQueryKeys.all, "list"],
  detail: (id) => [...roleQueryKeys.all, "detail", id],
  create: () => [...roleQueryKeys.all, "create"],
  update: () => [...roleQueryKeys.all, "update"],
  delete: () => [...roleQueryKeys.all, "delete"],
  manageroles: () => [...roleQueryKeys.all, "manageroles"],
};

export const useGetAllRolesQuery = (options = {}) => {
  return useQuery({
    queryKey: roleQueryKeys.list(),
    queryFn: roleService.getAllRoles,
    ...QUERY_CONFIGS.STATIC,
    ...options,
  });
};

// GET ROLE BY ID
export const useGetRoleByIdQuery = (id, options = {}) => {
  const request = useCallback(async () => {
    try {
      const response = await roleService.getRoleById(id);
      return response;
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(
        error,
        "Failed to fetch role details",
      );
      throw new Error(errorMessage);
    }
  }, [id]);

  return useQuery({
    queryKey: roleQueryKeys.detail(id),
    queryFn: request,
    enabled: !!id && options.enabled !== false,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useCreateRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleData) => roleService.createRole(roleData),
    mutationKey: roleQueryKeys.create(),
    onMutate: async (newRole) => {
      await queryClient.cancelQueries({ queryKey: roleQueryKeys.list() });

      const previousRoles = queryClient.getQueryData(roleQueryKeys.list());
      const optimisticRole = {
        id: `temp-${Date.now()}`,
        name: newRole.name,
        permissions:
          newRole.permissions?.map((pid) => ({
            id: pid,
            name: "loading...",
            description: "Loading permission info...",
          })) ?? [],
      };
      queryClient.setQueryData(roleQueryKeys.list(), (old = []) => [
        ...old,
        optimisticRole,
      ]);

      return { previousRoles };
    },
    onError: (error, _newPermission, context) => {
      if (context?.previousRoles) {
        queryClient.setQueryData(roleQueryKeys.list(), context.previousRoles);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleQueryKeys.list() });
    },
    retry: false,
  });
};

export const useUpdateRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roleData }) => roleService.updateRole(id, roleData),
    mutationKey: roleQueryKeys.update(),
    onMutate: async ({ id, roleData }) => {
      await queryClient.cancelQueries({ queryKey: roleQueryKeys.list() });

      const previousRoles = queryClient.getQueryData(roleQueryKeys.list());

      queryClient.setQueryData(roleQueryKeys.list(), (old = []) =>
        old.map((role) =>
          role.id === id
            ? {
                ...role,
                ...roleData,
                permissions:
                  roleData.permissions?.map((pid) => ({
                    id: pid,
                    name: "loading...",
                    description: "Loading permission info...",
                  })) ?? [],
              }
            : role,
        ),
      );

      return { previousRoles };
    },

    onError: (error, _newPermission, context) => {
      if (context?.previousRoles) {
        queryClient.setQueryData(roleQueryKeys.list(), context.previousRoles);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: roleQueryKeys.list() });
      queryClient.invalidateQueries({
        queryKey: roleQueryKeys.detail(variables.id),
      });
    },
    retry: false,
  });
};

export const useDeleteRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => roleService.deleteRole(id),
    mutationKey: roleQueryKeys.delete(),

    // Optimistic update
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: roleQueryKeys.list() });

      // Snapshot previous roles
      const previousRoles = queryClient.getQueryData(roleQueryKeys.list());

      // Optimistically remove the role from cache
      queryClient.setQueryData(roleQueryKeys.list(), (old = []) =>
        old.filter((role) => role.id !== id),
      );

      // Return rollback context
      return { previousRoles };
    },

    // Rollback on error
    onError: (error, _id, context) => {
      if (context?.previousRoles) {
        queryClient.setQueryData(roleQueryKeys.list(), context.previousRoles);
      }
    },

    // Always refetch after mutation settles
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: roleQueryKeys.list() });
      queryClient.removeQueries({ queryKey: roleQueryKeys.detail(id) });
    },

    retry: false,
  });
};

export const useAssignRolesToUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roleIds }) =>
      roleService.assignRoleToUser({ userId, roleIds }),
    mutationKey: roleQueryKeys.manageroles(),

    onMutate: async ({ userId, roleIds }) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKeys.users() });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });

      const previousUsers = queryClient.getQueryData(profileQueryKeys.users());
      const previousUserDetail = queryClient.getQueryData(
        profileQueryKeys.userDetail(userId),
      );

      const allRoles = queryClient.getQueryData(roleQueryKeys.list()) || [];
      const assignedRoleNames = roleIds.map((roleId) => {
        const role = allRoles.find((r) => r.id === roleId);
        return role ? role.name : `ROLE_${roleId}`;
      });

      if (previousUsers) {
        queryClient.setQueryData(profileQueryKeys.users(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, roles: assignedRoleNames } : user,
          ),
        );
      }

      if (previousUserDetail) {
        queryClient.setQueryData(profileQueryKeys.userDetail(userId), {
          ...previousUserDetail,
          roles: assignedRoleNames,
        });
      }

      return { previousUsers, previousUserDetail };
    },

    // Rollback on error
    onError: (error, { userId }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          profileQueryKeys.users(),
          context.previousUsers,
        );
      }
      if (context?.previousUserDetail) {
        queryClient.setQueryData(
          profileQueryKeys.userDetail(userId),
          context.previousUserDetail,
        );
      }
    },

    onSuccess: (data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.users() });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.merchants() });
      queryClient.invalidateQueries({ queryKey: authQueryKeys.currentUser() });
    },

    onSettled: (data, error, { userId }) => {
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.users() });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.merchants() });
      queryClient.invalidateQueries({ queryKey: authQueryKeys.currentUser() });
    },

    retry: false,
  });
};
